import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Cpu, Eye, Award, Shield, CheckCircle, Sparkles } from 'lucide-react';

const GRADING_GRAPHIC_URL = 'https://customer-assets.emergentagent.com/job_nice-grading-app/artifacts/1q9z5mg7_Screenshot%202026-01-20%20at%205.59.19%E2%80%AFPM.png';

const GradingStandardsPage = () => {
  const gradingScale = [
    { grade: '10', label: 'Gem Mint', description: 'A virtually perfect card. Four sharp corners, sharp focus, full original gloss, and free of staining. A 10 must have no print spots or surface scratches.' },
    { grade: '9', label: 'Mint', description: 'A superb condition card with only one minor flaw. A corner or edge with slight damage, a tiny amount of print spots, or color or focus imperfection.' },
    { grade: '8', label: 'Near Mint-Mint', description: 'A super high-end card with a minor flaw. Centering slightly off or a minimal print spot or surface wear visible upon close inspection.' },
    { grade: '7', label: 'Near Mint', description: 'A quality card with some minor wear including slightly rounded corners, minor chipping on edges, and light surface wear.' },
    { grade: '6', label: 'Excellent-Mint', description: 'A card with visible wear on corners, edges, and surfaces but still maintains overall visual appeal.' },
    { grade: '5', label: 'Excellent', description: 'A card showing obvious wear including moderate corner wear, edge chipping, and surface wear.' },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-[#002554] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Grading Standards
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Understanding how NICE Grading evaluates your cards with precision, consistency, and collector-focused expertise
            </p>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment to Excellence</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                At NICE Grading, we believe that every card tells a story. Whether it's a vintage rookie card passed down through generations or a modern chase card fresh from the pack, each piece deserves to be evaluated with the utmost care and precision. Our grading standards have been developed through years of industry experience, collector feedback, and continuous refinement to ensure that every grade we assign is accurate, consistent, and trusted by collectors worldwide.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                What sets NICE Grading apart is our unwavering commitment to transparency. We don't just tell you the grade—we help you understand <em>why</em> your card received that grade. This educational approach empowers collectors to make informed decisions about their collections and future acquisitions.
              </p>
            </div>
          </div>
        </section>

        {/* Nivora Intelligence Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center bg-[#003594]/10 rounded-full px-4 py-2 mb-6">
                  <Cpu className="w-5 h-5 text-[#003594] mr-2" />
                  <span className="text-[#003594] font-semibold">Proprietary Technology</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Nivora Intelligence™
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  While human expertise remains at the core of our grading process, we've developed Nivora Intelligence™—our proprietary grading methodology that combines advanced imaging technology with decades of collective grading experience. This system ensures that every card is evaluated against consistent, objective criteria while still preserving the nuanced judgment that only trained human eyes can provide.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Nivora Intelligence™ assists our graders by providing high-resolution analysis of centering measurements, edge consistency, and surface anomalies. However, the final grade is always determined by our expert graders who understand that a card's appeal goes beyond mere measurements—it's about the complete aesthetic experience.
                </p>
                <div className="grid grid-cols-2 gap-4 mt-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#003594] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Consistent evaluations</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#003594] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Advanced imaging</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#003594] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Human expertise</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#003594] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Objective criteria</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#003594] rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-6">How Nivora Intelligence™ Works</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">High-Resolution Scanning</h4>
                      <p className="text-gray-300 text-sm">Cards are scanned at ultra-high resolution to capture every detail</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Algorithmic Analysis</h4>
                      <p className="text-gray-300 text-sm">Our system analyzes centering, edges, and surface anomalies</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Expert Review</h4>
                      <p className="text-gray-300 text-sm">Trained graders review findings and apply professional judgment</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="font-bold">4</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Final Grade Assignment</h4>
                      <p className="text-gray-300 text-sm">A comprehensive grade is assigned based on all criteria</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Grading Graphic Section */}
        <section className="py-16 bg-[#002554]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <img 
              src={GRADING_GRAPHIC_URL} 
              alt="NICE Grading Criteria - Centering, Edges, Corners, Surface" 
              className="w-full rounded-xl shadow-2xl"
            />
          </div>
        </section>

        {/* Four Pillars of Grading */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">The Four Pillars of Card Grading</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Every card submitted to NICE Grading is evaluated across four critical dimensions. Understanding these pillars helps collectors appreciate the comprehensive nature of our grading process.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Centering */}
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <div className="w-12 h-12 bg-[#003594] rounded-xl flex items-center justify-center mb-6">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Centering</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Centering refers to the positioning of the card's image within its borders. Under controlled lighting conditions, our graders meticulously measure each border to ensure aesthetically pleasing dimensions. Perfect centering means equal borders on all sides, creating visual balance that collectors highly value.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our Nivora Intelligence™ system calculates centering ratios with precision, but our graders understand that acceptable centering can vary by era and manufacturer. Vintage cards, for example, are evaluated with historical production standards in mind.
                </p>
              </div>

              {/* Corners */}
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <div className="w-12 h-12 bg-[#003594] rounded-xl flex items-center justify-center mb-6">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Corners</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Under 10x magnification, each corner is examined for crisp, sharp lines. Collectors value great-looking corners because they're often the first area to show wear. Even the slightest rounding, fraying, or damage can significantly impact a card's grade.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our graders are trained to identify subtle corner wear that might be invisible to the naked eye but becomes apparent under magnification. This meticulous attention to detail ensures accurate and fair grading.
                </p>
              </div>

              {/* Edges */}
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <div className="w-12 h-12 bg-[#003594] rounded-xl flex items-center justify-center mb-6">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Edges</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  A 360° evaluation of every edge identifies chipping, whitening, unnatural cuts, and other imperfections. Great edges equal a great-looking card. Edge wear is particularly common in cards that have been handled frequently or stored improperly.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our graders examine edges from multiple angles and lighting conditions to ensure no defects are missed. We also look for factory-related edge issues that might indicate the card was damaged during the manufacturing process.
                </p>
              </div>

              {/* Surface */}
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <div className="w-12 h-12 bg-[#003594] rounded-xl flex items-center justify-center mb-6">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Surface</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  The front and back surfaces are scrutinized for scratches, print defects, and handling marks. Collectors understand that a clean surface is a clean card. Surface quality significantly impacts both the visual appeal and long-term value of a collectible.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We evaluate surface gloss, print quality, color saturation, and any post-production damage. Our graders are trained to distinguish between manufacturing variations and handling-related damage.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Grading Scale */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Grading Scale</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                NICE Grading uses a 1-10 scale that has become the industry standard. Here's what each grade means for your cards.
              </p>
            </div>

            <div className="space-y-4">
              {gradingScale.map((item) => (
                <div key={item.grade} className="bg-white rounded-xl p-6 border border-gray-200 flex items-start gap-6">
                  <div className="w-16 h-16 bg-[#003594] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-white">{item.grade}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.label}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
              <p className="text-center text-gray-500 text-sm mt-6">
                Grades 1-4 represent cards with significant wear. Contact us for detailed information about lower grade criteria.
              </p>
            </div>
          </div>
        </section>

        {/* Human Element */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">The Human Element</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Grading is not for robots, because robots can't feel the joy of an aesthetically pleasing card. First-class grading is human observation, experience, and expertise applied to the card at hand. Our professional graders examine the condition and aesthetic of each card carefully, thoroughly, and without compromise... every time.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                While technology plays an important supporting role in our process, we firmly believe that the art of card grading requires human judgment. A card's value isn't just in its measurable attributes—it's in the complete visual experience it provides. Our graders understand the nuances of different eras, manufacturers, and card types, bringing context and expertise that no algorithm can replicate.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Each of our graders undergoes rigorous training and certification before ever touching a customer's card. They're not just employees—they're collectors themselves who understand the passion and investment behind every submission. This collector-centric approach ensures that every card is treated with the respect and care it deserves.
              </p>
            </div>
          </div>
        </section>

        {/* Consistency Promise */}
        <section className="py-16 bg-[#003594]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Our Consistency Promise</h2>
            <p className="text-gray-200 text-lg leading-relaxed mb-8">
              We understand that grade consistency is crucial for collector confidence. That's why every card goes through multiple review stages, and our graders are regularly calibrated to ensure uniform standards across all submissions. When you see a NICE grade, you can trust it represents the same quality standard whether the card was graded yesterday or last year.
            </p>
            <Link to="/new-submission">
              <Button className="bg-white text-[#003594] hover:bg-gray-100 px-8 py-6 text-base font-semibold">
                Submit Your Cards Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GradingStandardsPage;
